import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  ListItemIcon, Menu, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, Typography,
} from "@mui/material";
import { AddOutlined, ContentCopyOutlined, DeleteOutlined, EditOutlined, MoreVert } from "@mui/icons-material";
import moment from "moment-timezone";
import { toast } from "react-toastify";
import { api } from "../../services/api";
import useGetFechasEvento from "../../hooks/useGetFechasEvento";
import useEventoLimitesStore from "../../stores/useEventoLimitesStore";
import BotonCircular from "../ui/BotonCircular";
import FilasSkeleton from "../ui/FilasSkeleton";
import FormularioTarifa from "./FormularioTarifa";
import { TIPO } from "../../domain/tarifa/schema";

const PRIMARIO = "#5656FF";
const TITULO = "#0b0062";
const FUENTE = "Bricolage Grotesque";
const cabeceraSx = { fontSize: 15, fontWeight: 600, color: "#0B0060", fontFamily: FUENTE };
const celdaSx = { fontFamily: FUENTE, whiteSpace: "nowrap" };

// el backend devuelve hasta 500 por pedido; ningún evento llega a tantas
const MAX_TARIFAS = 500;

const fechaHora = (valor) => (valor ? moment(valor).format("DD/MM/YYYY HH:mm") : "-");

const precioTexto = (precio) =>
  Number(precio) > 0 ? `$ ${Number(precio).toLocaleString("es-AR", { minimumFractionDigits: 2 })}` : "Gratis";

const Acciones = ({ tarifa, onEditar, onDuplicar, onEliminar }) => {
  const [menu, setMenu] = useState(null);
  const elegir = (accion) => {
    setMenu(null);
    accion(tarifa);
  };

  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
      <BotonCircular icono={<EditOutlined fontSize="small" />} titulo="Editar" onClick={() => onEditar(tarifa)} />
      <IconButton size="small" aria-label="Más acciones" onClick={(e) => setMenu(e.currentTarget)}>
        <MoreVert fontSize="small" />
      </IconButton>
      <Menu anchorEl={menu} open={Boolean(menu)} onClose={() => setMenu(null)}>
        <MenuItem onClick={() => elegir(onDuplicar)}>
          <ListItemIcon><ContentCopyOutlined fontSize="small" /></ListItemIcon>
          Duplicar
        </MenuItem>
        <MenuItem onClick={() => elegir(onEliminar)} sx={{ color: "#ef4444" }}>
          <ListItemIcon><DeleteOutlined fontSize="small" sx={{ color: "#ef4444" }} /></ListItemIcon>
          Eliminar
        </MenuItem>
      </Menu>
    </Box>
  );
};

const SeccionTarifas = ({ idtipo, tarifas, cargando, ubicaciones, soloLectura, onNueva, ...acciones }) => {
  const [pagina, setPagina] = useState(0);
  const [porPagina, setPorPagina] = useState(10);
  const esTicket = idtipo === TIPO.TICKET;
  // al borrar la última fila de una página, volver a una que exista
  const paginaVisible = Math.min(pagina, Math.max(0, Math.ceil(tarifas.length / porPagina) - 1));
  const visibles = tarifas.slice(paginaVisible * porPagina, (paginaVisible + 1) * porPagina);
  const nombreUbicacion = (id) => ubicaciones.find((u) => u.id === id)?.ubicacion ?? "-";
  const concepto = (id) => tarifas.find((t) => t.id === id)?.concepto;
  const columnas = soloLectura ? 5 : 6;

  return (
    <Box sx={{ mb: 5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 2 }}>
        <Typography sx={{ fontFamily: FUENTE, fontSize: 20, fontWeight: 600, color: TITULO }}>
          {esTicket ? "Tickets" : "Consumiciones"}
        </Typography>
        {!soloLectura && (
          <Button
            variant="contained"
            startIcon={<AddOutlined />}
            onClick={onNueva}
            sx={{ bgcolor: PRIMARIO, textTransform: "none", fontFamily: FUENTE, "&:hover": { bgcolor: "#4545ee" } }}
          >
            {esTicket ? "Nuevo ticket" : "Nueva consumición"}
          </Button>
        )}
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: "12px", boxShadow: "0px 2px 8px rgba(0,0,0,0.08)", border: "1px solid #E6E6FF" }}>
        <Table aria-label={esTicket ? "Tickets del evento" : "Consumiciones del evento"}>
          <TableHead sx={{ bgcolor: "#E6E6FF" }}>
            <TableRow>
              <TableCell sx={cabeceraSx}>Nombre</TableCell>
              <TableCell sx={cabeceraSx}>Precio</TableCell>
              <TableCell sx={cabeceraSx}>{esTicket ? "Ubicación" : "Límites"}</TableCell>
              <TableCell sx={cabeceraSx}>Inicio de venta</TableCell>
              <TableCell sx={cabeceraSx}>Fin de venta</TableCell>
              {!soloLectura && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {cargando ? (
              <FilasSkeleton columnas={columnas} filas={3} />
            ) : tarifas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columnas} align="center">
                  <Alert severity="info" sx={{ display: "inline-flex", fontFamily: FUENTE }}>
                    {esTicket ? "Todavía no hay tickets." : "Todavía no hay consumiciones."}
                  </Alert>
                </TableCell>
              </TableRow>
            ) : (
              visibles.map((tarifa) => (
                <TableRow key={tarifa.id} hover>
                  <TableCell sx={{ fontFamily: FUENTE, color: "#0b0060" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      {tarifa.concepto}
                      {tarifa.privado && <Chip label="Privada" size="small" />}
                      {tarifa.fechas?.some((f) => f.fecha?.oculta) && <Chip label="Fecha oculta" size="small" />}
                      {tarifa.idtarifa_previa && (
                        <Chip label={`Tanda: al agotarse ${concepto(tarifa.idtarifa_previa) ?? "la anterior"}`} size="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={celdaSx}>{precioTexto(tarifa.precio)}</TableCell>
                  <TableCell sx={celdaSx}>
                    {esTicket
                      ? nombreUbicacion(tarifa.idubicacion)
                      : `${tarifa.limite_persona ?? "-"} por comprador · ${tarifa.limite_maximo ?? "-"} en total`}
                  </TableCell>
                  <TableCell sx={celdaSx}>{fechaHora(tarifa.inicio_venta)}</TableCell>
                  <TableCell sx={celdaSx}>{fechaHora(tarifa.fin_venta)}</TableCell>
                  {!soloLectura && (
                    <TableCell align="right">
                      <Acciones tarifa={tarifa} {...acciones} />
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {tarifas.length > 10 && (
          <TablePagination
            component="div"
            count={tarifas.length}
            page={paginaVisible}
            onPageChange={(_, nueva) => setPagina(nueva)}
            rowsPerPage={porPagina}
            rowsPerPageOptions={[10, 25, 50]}
            onRowsPerPageChange={(e) => {
              setPorPagina(parseInt(e.target.value, 10));
              setPagina(0);
            }}
            labelRowsPerPage="Filas por página"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        )}
      </TableContainer>
    </Box>
  );
};

const listaVacia = { tarifas: [], cargando: true };

const GestionTarifas = ({ eventoId, soloLectura = false }) => {
  // cada tabla carga por separado: guardar un ticket no recarga las consumiciones
  const [listas, setListas] = useState({ [TIPO.TICKET]: listaVacia, [TIPO.VOUCHER]: listaVacia });
  const [ubicaciones, setUbicaciones] = useState([]);
  const [editor, setEditor] = useState(null); // { idtipo, tarifa, duplicar } mientras el formulario está abierto
  const [aEliminar, setAEliminar] = useState(null);
  const [eliminando, setEliminando] = useState(false);
  const { fechasEvento = [] } = useGetFechasEvento(eventoId);
  const { fetchLimitesEvento, getLimitesEvento } = useEventoLimitesStore();
  const esPlanGratuito = getLimitesEvento(eventoId)?.es_plan_gratuito === true;

  const actualizarLista = useCallback(
    (idtipo, cambios) => setListas((actuales) => ({ ...actuales, [idtipo]: { ...actuales[idtipo], ...cambios } })),
    []
  );

  const cargar = useCallback(async (idtipo) => {
    actualizarLista(idtipo, { cargando: true });
    try {
      const respuesta = await api.tarifas.getAll(eventoId, "", MAX_TARIFAS, 1, idtipo);
      if (respuesta.status !== "success") throw new Error(respuesta.message);
      actualizarLista(idtipo, { tarifas: respuesta.data?.tarifas ?? [], cargando: false });
    } catch (error) {
      toast.error(error.message || "No se pudieron cargar las tarifas.");
      actualizarLista(idtipo, { cargando: false });
    }
  }, [eventoId, actualizarLista]);

  const cargarUbicaciones = useCallback(async () => {
    try {
      const respuesta = await api.ubicacionesNew.getAllCompletas(eventoId);
      if (respuesta.status !== "success") return;
      const lista = Array.isArray(respuesta.data) ? respuesta.data : respuesta.data?.ubicaciones;
      setUbicaciones(Array.isArray(lista) ? lista : []);
    } catch {
      toast.error("No se pudieron cargar las ubicaciones.");
    }
  }, [eventoId]);

  useEffect(() => {
    if (!eventoId) return;
    cargar(TIPO.TICKET);
    cargar(TIPO.VOUCHER);
    cargarUbicaciones();
    fetchLimitesEvento(eventoId);
  }, [eventoId, cargar, cargarUbicaciones, fetchLimitesEvento]);

  // un ticket nuevo puede haber creado una ubicación
  const guardada = () => {
    const { idtipo } = editor;
    setEditor(null);
    cargar(idtipo);
    if (idtipo === TIPO.TICKET) cargarUbicaciones();
  };

  const eliminar = async () => {
    setEliminando(true);
    try {
      const respuesta = await api.tarifas.deleteTarifa(aEliminar.id);
      if (respuesta.status !== "success") throw new Error(respuesta.message);
      toast.success("Tarifa eliminada.");
      actualizarLista(aEliminar.idtipo, {
        tarifas: listas[aEliminar.idtipo].tarifas.filter((t) => t.id !== aEliminar.id),
      });
      setAEliminar(null);
    } catch (error) {
      toast.error(error.message || "No se pudo eliminar la tarifa.");
    } finally {
      setEliminando(false);
    }
  };

  const seccion = (idtipo) => (
    <SeccionTarifas
      idtipo={idtipo}
      tarifas={listas[idtipo].tarifas}
      cargando={listas[idtipo].cargando}
      ubicaciones={ubicaciones}
      soloLectura={soloLectura}
      onNueva={() => setEditor({ idtipo, tarifa: null })}
      onEditar={(tarifa) => setEditor({ idtipo, tarifa })}
      onDuplicar={(tarifa) => setEditor({ idtipo, tarifa, duplicar: true })}
      onEliminar={setAEliminar}
    />
  );

  return (
    <Box>
      {seccion(TIPO.TICKET)}
      {seccion(TIPO.VOUCHER)}

      {editor && (
        <FormularioTarifa
          eventoId={eventoId}
          idtipo={editor.idtipo}
          tarifa={editor.tarifa}
          duplicar={editor.duplicar}
          fechasEvento={fechasEvento}
          ubicaciones={ubicaciones}
          tarifasTickets={listas[TIPO.TICKET].tarifas}
          esPlanGratuito={esPlanGratuito}
          onCerrar={() => setEditor(null)}
          onGuardada={guardada}
        />
      )}

      <Dialog open={Boolean(aEliminar)} onClose={() => !eliminando && setAEliminar(null)}>
        <DialogTitle sx={{ fontFamily: FUENTE, fontSize: 18 }}>¿Eliminar tarifa?</DialogTitle>
        <DialogContent>
          <Typography>
            <strong>{aEliminar?.concepto}</strong> se elimina con su configuración. Si ya tiene ventas no se puede
            eliminar: en ese caso cambiá su fin de venta para dejar de venderla.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAEliminar(null)} disabled={eliminando} sx={{ textTransform: "none", color: "#374151" }}>
            Cancelar
          </Button>
          <Button variant="outlined" color="error" onClick={eliminar} disabled={eliminando} sx={{ textTransform: "none" }}>
            {eliminando ? "Eliminando..." : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GestionTarifas;
